import * as http from './http'
import endpoints from './endpoints'
import { CheckoutPageModel } from '@ViewModels/CheckoutPageModel'
import { CheckoutDto } from '@ViewModels/CheckoutDto'
import { CheckoutStreetAddressDto } from '@ViewModels/CheckoutStreetAddressDto'

const apiCheckout = endpoints.checkout
const apiAddressFinder = endpoints.addressfinder

export const getModelData = async (): Promise<CheckoutPageModel> => {
   const url = `${apiCheckout}`
   return http.get(url)
}

export const verifyCheckout = async (dto: CheckoutDto): Promise<CommitResult> => {
   const url = `${apiCheckout}/verify`
   return (await http.post(url, dto)) as CommitResult
}

export const completeCheckout = async (dto: CheckoutDto): Promise<CommitResult> => {
   const url = `${apiCheckout}`
   return (await http.post(url, dto)) as CommitResult
}

export const findAddress = async (query: string, country: string) => {
   const url = `${apiAddressFinder}/AddressSearch?query=${query}&country=${country}`
   const result = await http.get(url)
   return result
}

export const addressDetails = async (addressId: string, country: string) => {
   const url = `${apiAddressFinder}/GetAddress?addressId=${addressId}&country=${country}`
   const result = await http.get(url)
   return transformAddress(country, result)
}

const transformAddress = (country: string, address: any): CheckoutStreetAddressDto => {
   if (country.toLowerCase() === 'nz') {
      return transformNewZealandAddress(address)
   } else if (country.toLowerCase() === 'au') {
      return transformAustralianAddress(address)
   } else {
      return transformInternationalAddress(address, country)
   }
}

const transformNewZealandAddress = (result: NewZealandAddress): CheckoutStreetAddressDto => {
   const _ = convertNullToEmptyString
   let line1 = `${_(result.Street_Number)}${_(result.Street_Alpha)} ${_(result.Street)} ${_(
      result.Street_Type
   )}`
   if (result.Unit_Type) {
      line1 = `${result.Unit_Type} ${_(result.Unit_Value)}, ${line1}`
   }
   let line2: string = ''
   if (result.Is_Rural_Delivery) {
      line2 = `${_(result.Rural_Delivery_Number)}`
   } else if (result.Building_Name) {
      if (result.Floor) {
         line2 = `Floor ${result.Floor}, ${result.Building_Name}`
      } else {
         line2 = result.Building_Name
      }
   }
   const address: CheckoutStreetAddressDto = {
      AddressLine1: line1.trim(),
      AddressLine2: line2.trim(),
      AddressLine3: _(result.Suburb),
      City: _(result.City),
      Region: '',
      Postcode: _(result.Postcode),
      Country: _(result.Country),
      CountryId: '',
      OtherInformation: '',
      ContactPhoneNumber: '',
      Type: 'Residential',
   }
   return address
}

const transformAustralianAddress = (result: AustralianAddress): CheckoutStreetAddressDto => {
   const _ = convertNullToEmptyString
   const line1 = `${_(result.Street_Number)} ${_(result.Street)} ${_(result.Street_Type)}`

   const address: CheckoutStreetAddressDto = {
      AddressLine1: line1.trim(),
      AddressLine2: '',
      AddressLine3: _(result.Suburb),
      City: _(result.City),
      Region: _(result.State_Code),
      Postcode: _(result.Postcode),
      Country: _(result.Country),
      CountryId: '',
      OtherInformation: '',
      ContactPhoneNumber: '',
      Type: 'Residential',
   }
   return address
}

const transformInternationalAddress = (
   place: any,
   countryCode: string
): CheckoutStreetAddressDto => {
   const _ = convertNullToEmptyString
   const result = googleMapsPlaceToAddress(place, countryCode)

   let line1 = `${_(result.number)} ${_(result.street)}`
   if (!line1.trim().length) {
      line1 = result.premise
   }

   const address: CheckoutStreetAddressDto = {
      AddressLine1: line1.trim(),
      AddressLine2: _(result.line2),
      AddressLine3: _(result.suburb),
      City: _(result.city),
      Region: _(result.region),
      Postcode: _(result.postcode),
      Country: _(result.country),
      CountryId: '',
      OtherInformation: '',
      ContactPhoneNumber: '',
      Type: 'Residential',
   }
   return address
}

const convertNullToEmptyString = (value: any) => (value ? value : '')

const componentForm: { [index: string]: string } = {
   street_number: 'short_name',
   route: 'long_name',
   premise: 'long_name',
   sublocality_level_1: 'long_name',
   sublocality_level_2: 'long_name',
   sublocality: 'long_name',
   locality: 'long_name',
   administrative_area_level_1: 'short_name',
   administrative_area_level_2: 'long_name',
   country: 'long_name',
   postal_town: 'short_name',
   postal_code: 'short_name',
}

const googleMapsPlaceToAddress = (place: any, countryCode: string) => {
   countryCode = countryCode.toLowerCase()

   const location = {
      premise: '',
      address: '',
      number: '',
      street: '',
      line2: '',
      suburb: '',
      city: '',
      region: '',
      postcode: '',
      country: '',
   }

   // Set the full formatted address
   location.address = place.formatted_address || place.name

   // Get each component of the address from the place details
   // and fill the corresponding field in the result object
   if (place.address_components) {
      for (const addressComponent of place.address_components) {
         const addressType = addressComponent.types[0]
         const component = componentForm[addressType]

         if (component) {
            const val = addressComponent[component]

            if (addressType === 'street_number') {
               location.number = val
            }

            if (addressType === 'route') {
               location.street = val
            }

            if (addressType === 'premise') {
               location.premise = val
            }

            if (addressType === 'locality') {
               location.city = val
            }

            if (addressType === 'sublocality_level_2') {
               location.line2 = val
            }

            if (addressType === 'sublocality_level_1') {
               location.suburb = val
            }

            if (addressType === 'administrative_area_level_1') {
               location.region = val
            }

            if (addressType === 'country') {
               location.country = val
            }

            if (addressType === 'postal_code') {
               location.postcode = val
            }

            if (countryCode === 'uk') {
               if (addressType === 'postal_town') {
                  location.suburb = val
               }
               if (addressType === 'administrative_area_level_2') {
                  location.city = val
               }
            }
            if (countryCode === 'au') {
               if (addressType === 'administrative_area_level_2') {
                  location.city = val
               }
               if (addressType === 'locality') {
                  location.suburb = val
               }
            }
            if (countryCode === 'nz') {
               if (addressType === 'locality') {
                  location.region = val
               }
               if (addressType === 'sublocality_level_1') {
                  location.city = val
               }
            }
         }
      }
   }
   return location
}

interface AddressElement {
   label: string
   visible?: boolean
   required?: boolean
}

interface NewZealandAddress {
   Building_Name: string
   Floor: string
   Postcode: string
   Street: string
   Street_Alpha: string
   Street_Number: string
   Street_Type: string
   Suburb: string
   City: string
   Unit_Type: string
   Unit_Value: string
   Country: string
   Is_Rural_Delivery: boolean
   Rural_Delivery_Number: string
}

interface AustralianAddress {
   Postcode: string
   Street: string
   Street_Number: string
   Street_Type: string
   Suburb: string
   City: string
   Country: string
   State: string
   State_Code: string
}

const defaultAddressConfiguration: { [key: string]: AddressElement } = {
   addressLine1: { label: 'Address Line 1', visible: true, required: true },
   addressLine2: { label: 'Address Line 2', visible: true },
   addressLine3: { label: 'Address Line 3', visible: true },
   city: { label: 'City', visible: true },
   region: { label: 'Region/State', visible: true },
   postcode: { label: 'Postcode', visible: true, required: true },
   country: { label: 'Country', visible: true }, // handled automatically as countryId
   contactPhoneNumber: { label: 'Contact Phone Number', visible: true, required: true },
   otherInformation: { label: 'Other Information', visible: true },
}
